/**
 * DRAFT / PUBLISH V1 frontend API client.
 *
 * Publish/Republish is an authoritative write reusing the EXACT hardened
 * authority (SIWE session cookie + memory-only session-bound CSRF shared with
 * territory-identity-api + browser Origin). The client NEVER sends canvas
 * contents, GP, media refs, or a publication revision — only the expected
 * SAVED-draft/identity authority it last observed. The public read requires no
 * auth and returns the immutable server snapshot only.
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class PublicationApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "PublicationApiError";
    this.status = status;
    this.code = code;
  }
}

interface ErrorEnvelope { code?: string; message?: string; detail?: { code?: string; message?: string } }

export interface PublicationState {
  identity_id: string;
  state: "UNPUBLISHED" | "PUBLISHED_CURRENT" | "DRAFT_CHANGED" | "TERRITORY_CHANGED";
  has_active_publication: boolean;
  draft_changed: boolean;
  territory_changed: boolean;
  current_draft_revision: number;
  current_identity_revision: number;
  current_membership_fingerprint: string;
  publishable: boolean;
  gp: { capacity_gp: number; used_gp: number; available_gp: number; over_capacity: boolean };
  can_publish: boolean;
  publish_blocked_reason: string | null;
  publication: null | {
    publication_revision: number;
    source_draft_revision: number;
    source_identity_revision: number;
    source_membership_fingerprint: string;
    published_at: string | null;
    gp: Record<string, number | boolean>;
  };
}

export interface PublishedScene3D {
  work_arts: import("../domain/voxel3d").WorkArt[];
  voxel_count: number;
  capacity_snapshot?: {
    used_voxels: number;
    capacity_voxels: number;
    available_voxels: number;
    over_capacity: boolean;
  };
}

export interface PublicSnapshot {
  identity_id: string;
  display_name: string | null;
  publication_revision: number;
  elements: Array<Record<string, unknown>>;
  style: Record<string, unknown>;
  footprint_epochs: Record<string, unknown>;
  gp: Record<string, number | boolean>;
  scene_3d: PublishedScene3D | null;
  published_media_asset_ids: string[];
  published_at: string | null;
}

async function parse(resp: Response): Promise<unknown> {
  const text = await resp.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    throw new PublicationApiError(resp.status, env.code ?? env.detail?.code ?? "PUBLICATION_ERROR",
      env.message ?? env.detail?.message ?? resp.statusText);
  }
  return body;
}

export async function getPublicationState(identityId: string, signal?: AbortSignal): Promise<PublicationState> {
  const resp = await fetch(`${API_BASE}/api/v1/publications/${encodeURIComponent(identityId)}`, {
    credentials: "include", cache: "no-store", signal,
  });
  return ((await parse(resp)) as { publication: PublicationState }).publication;
}

export async function publishTerritory(
  identityId: string,
  expected: { expected_draft_revision: number; expected_identity_revision: number; expected_membership_fingerprint: string },
  signal?: AbortSignal,
): Promise<PublicationState> {
  const body = JSON.stringify(expected);
  const doPost = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}/api/v1/publications/${encodeURIComponent(identityId)}/publish`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body,
      signal,
    });
  let resp = await doPost(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPost(await getCsrfToken(true));
    }
  }
  return ((await parse(resp)) as { publication: PublicationState }).publication;
}

/** Public snapshot read (no auth). Returns null when no active publication. */
export async function getPublicPublication(identityId: string, signal?: AbortSignal): Promise<PublicSnapshot | null> {
  const resp = await fetch(`${API_BASE}/api/v1/publications/${encodeURIComponent(identityId)}/public`, {
    cache: "no-store", signal,
  });
  if (resp.status === 404) return null;
  return ((await parse(resp)) as { publication: PublicSnapshot }).publication;
}

/**
 * Bounded PUBLIC batch read for the Grid viewer. One request per discovery
 * generation (no N+1). `no-store` guarantees Republish/withdrawal are reflected
 * deterministically on the next discovery without arbitrary hard-refresh logic.
 * Returns only identities with a CURRENT active publication (fail-soft).
 */
export async function batchPublicPublications(
  identityIds: string[],
  signal?: AbortSignal,
): Promise<PublicSnapshot[]> {
  const unique = Array.from(new Set(identityIds.filter(Boolean)));
  if (unique.length === 0) return [];
  const resp = await fetch(
    `${API_BASE}/api/v1/publications/public-batch?identity_ids=${encodeURIComponent(unique.join(","))}`,
    { cache: "no-store", signal },
  );
  return ((await parse(resp)) as { publications: PublicSnapshot[] }).publications ?? [];
}

/** Public publication media bytes URL (revision-busted; publication-authoritative). */
export function publicationMediaContentUrl(identityId: string, assetId: string, revision: number): string {
  return `${API_BASE}/api/v1/publications/${encodeURIComponent(identityId)}/media/${encodeURIComponent(assetId)}/content?v=${revision}`;
}
